/* eslint-disable @typescript-eslint/no-explicit-any */
import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { terreiroSignupRepository } from "../repositories/terreiroSignupRepository";
import type {
    QuickTerreiroSignupPayload,
    QuickTerreiroSignupResponse,
    TerreiroSignupApiError,
} from "../types/terreiroSignup.types";
import { sessionQueryKey } from "./useSessionQuery";

/**
 * Normalized error type for terreiro signup errors
 */
export interface TerreiroSignupError {
  message: string;
  code?: string;
  fields?: Record<string, string>;
  status?: number;
}

/**
 * Normalizes various error shapes into a consistent TerreiroSignupError format.
 */
export function normalizeTerreiroSignupError(
  error: unknown,
): TerreiroSignupError {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as TerreiroSignupApiError | undefined;
    const status = error.response?.status;

    // Check for specific error codes from the API
    if (data?.code === "EMAIL_ALREADY_EXISTS") {
      return {
        message: data.message || "Este email já está em uso",
        code: "EMAIL_ALREADY_EXISTS",
        fields: { email: "Este email já está cadastrado" },
        status,
      };
    }

    if (data?.code === "PHONE_ALREADY_EXISTS") {
      return {
        message: data.message || "Este telefone já está em uso",
        code: "PHONE_ALREADY_EXISTS",
        fields: { phone: "Este telefone já está cadastrado" },
        status,
      };
    }

    if (data?.code === "TERREIRO_NAME_EXISTS") {
      return {
        message: data.message || "Já existe um terreiro com este nome",
        code: "TERREIRO_NAME_EXISTS",
        fields: { terreiroName: "Nome de terreiro já está em uso" },
        status,
      };
    }

    // Handle validation errors
    if (data?.code === "VALIDATION_ERROR" && data.fields) {
      return {
        message: data.message || "Erro de validação",
        code: "VALIDATION_ERROR",
        fields: data.fields,
        status,
      };
    }

    // Generic API error
    return {
      message: data?.message || error.message || "Erro ao cadastrar terreiro",
      code: data?.code,
      fields: data?.fields,
      status,
    };
  }

  // Handle generic Error objects
  if (error instanceof Error) {
    return {
      message: error.message || "Erro desconhecido ao cadastrar terreiro",
    };
  }

  // Fallback for unknown error types
  return {
    message: "Erro desconhecido ao cadastrar terreiro",
  };
}

/**
 * React Query mutation hook for quick terreiro signup.
 * Uses the /terreiro-signup/quick endpoint which requires only essential fields.
 *
 * This hook:
 * 1. Calls the quick signup API endpoint
 * 2. On success, updates the session cache with the new user data
 * 3. Stores terreiro and medium data in local storage
 * 4. Normalizes any errors into a consistent format
 */
export const useQuickTerreiroSignupMutation = (
  options?: UseMutationOptions<
    QuickTerreiroSignupResponse,
    TerreiroSignupError,
    QuickTerreiroSignupPayload
  >,
) => {
  const queryClient = useQueryClient();

  const wrappedOnSuccess: UseMutationOptions<
    QuickTerreiroSignupResponse,
    TerreiroSignupError,
    QuickTerreiroSignupPayload
  >["onSuccess"] = async (data, variables, context) => {
    try {
      if (data?.user) {
        const profile = data.user.profile;
        const terreiro = data.terreiro;
        const terreiroId = terreiro?.id;
        const medium = data.medium;
        const mediumId = medium?.id;

        // ──────────────────────────────────────────────────────────
        // 1. Update localStorage FIRST so that useSessionQuery picks
        //    up the new terreiroId on its next render cycle.
        // ──────────────────────────────────────────────────────────

        if (profile) {
          try {
            localStorage.setItem(
              "ilovemacumba:auth:currentUser",
              JSON.stringify(profile),
            );
          } catch {
            // ignore localStorage errors
          }
        }

        if (terreiroId && terreiro) {
          try {
            localStorage.setItem(
              "ilovemacumba:terreiro:current",
              JSON.stringify(terreiro),
            );
          } catch {
            // ignore localStorage errors
          }
        }

        if (mediumId && medium) {
          try {
            localStorage.setItem(
              "ilovemacumba:medium:current",
              JSON.stringify(medium),
            );
          } catch {
            // ignore localStorage errors
          }
        }

        if (data.token) {
          try {
            localStorage.setItem("ilovemacumba:auth:token", data.token);
          } catch {
            // ignore localStorage errors
          }
        }

        // ──────────────────────────────────────────────────────────
        // 2. Set session data on the CORRECT query keys.
        //    useSessionQuery uses [...sessionQueryKey, terreiroId ?? "personal"]
        //    so we need to set on the key that will be active AFTER navigation.
        //    Set on both "personal" (current key) and new terreiroId to avoid
        //    a loading flash during the key transition.
        // ──────────────────────────────────────────────────────────

        queryClient.setQueryData(
          [...sessionQueryKey, "personal"],
          data.user,
        );
        if (terreiroId) {
          queryClient.setQueryData(
            [...sessionQueryKey, terreiroId],
            data.user,
          );
        }

        // Also set React Query caches (non-localStorage)
        if (profile) {
          queryClient.setQueryData(["auth", "currentUser"], profile);
        }
        if (terreiroId && terreiro) {
          queryClient.setQueryData(["terreiro", terreiroId], terreiro);
          queryClient.setQueryData(["terreiro", "current"], terreiro);
        }
        if (mediumId && medium) {
          queryClient.setQueryData(["medium", mediumId], medium);
          queryClient.setQueryData(["medium", "current"], medium);
        }
      } else {
        // ensure at least non-null value until /auth/me refetch
        queryClient.setQueryData([...sessionQueryKey, "personal"], null);
      }

      // Background refetch to confirm session via /v1/auth/me.
      // Use prefix matching to invalidate all session-related queries.
      queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    } catch {
      // swallow cache/localStorage errors
    }

    options?.onSuccess?.(data, variables, context as any);
  };

  return useMutation<
    QuickTerreiroSignupResponse,
    TerreiroSignupError,
    QuickTerreiroSignupPayload
  >({
    mutationFn: (data: QuickTerreiroSignupPayload) =>
      terreiroSignupRepository.quickSignup(data).catch((err) => {
        throw normalizeTerreiroSignupError(err);
      }),
    ...options,
    onSuccess: wrappedOnSuccess,
  });
};

export default useQuickTerreiroSignupMutation;
